<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';

const ARM_GOLD_LABEL_TRANSLATIONS = {
  'agent-approved-quote': 'اعتمد العرض',
  'agent-rejected-quote': 'رفض العرض',
  'ai-auto-evaluated': 'تم تقييم AI',
  'ai-eval-error': 'خطأ تقييم AI',
  'ai-quote-blocked': 'عرض AI موقوف',
  'ai-quote-draft-ready': 'مسودة AI جاهزة',
  b2b: 'شركات / B2B',
  booking_inquiry: 'استفسار حجز',
  'bot-fu-23h': 'متابعة 23 ساعة',
  'bot-fu-30m': 'متابعة 30 دقيقة',
  'bot-fu-4h': 'متابعة 4 ساعات',
  'bot-fu-8h': 'متابعة 8 ساعات',
  'bot-got-city': 'تم تحديد المدينة',
  'bot-got-dates': 'تم تحديد التواريخ',
  'bot-got-guests': 'تم تحديد الأشخاص',
  'bot-got-hotel': 'تم تحديد الفندق',
  'bot-got-rooms': 'تم تحديد الغرف',
  'bot-qualified': 'مؤهل آلياً',
  'bot-welcome': 'ترحيب أرسل',
  'cold-lead': 'عميل بارد',
  'competitor-check-error': 'خطأ فحص منافسين',
  'competitor-checked': 'تم فحص المنافسين',
  'competitor-manual-required': 'فحص منافسين يدوي',
  'hot-lead': 'عميل ساخن',
  'human-required': 'يحتاج موظف',
  'manual-price-needed': 'تسعير يدوي',
  'need-competitor-check': 'يحتاج فحص منافسين',
  'needs-agent-approval': 'يحتاج اعتماد موظف',
  'needs-manual-price': 'يحتاج سعر يدوي',
  'needs-reprice': 'يحتاج إعادة تسعير',
  'not-booking': 'ليس طلب حجز',
  'outside-24h-template-required': 'يحتاج قالب واتساب',
  'quote-approved-sent': 'العرض المعتمد أرسل',
  ramadan_bookings_2026: 'حجوزات رمضان 2026',
  ramadan_vip: 'رمضان VIP',
  ramdan_vip2: 'رمضان VIP 2',
  'sla-agent-missed': 'تأخر رد الموظف',
  'sla-ai-rescue': 'إنقاذ AI للـ SLA',
  spam_irrelevant: 'سبام / غير مناسب',
  'vendor-error': 'خطأ مورد',
  'vendor-login-needed': 'يحتاج دخول مورد',
  'warm-lead': 'عميل دافئ',
  'waiting-customer': 'بانتظار العميل',
  'waiting-supplier': 'بانتظار المورد',
  vip: 'VIP',
};

const { content, createdAt, contentAttributes } = useMessageContext();
const openAiTitle = 'فتح تفاصيل AI لهذه المحادثة';

const readableTime = computed(() =>
  messageTimestamp(createdAt.value, 'LLL d, h:mm a')
);

const stripHtml = value =>
  String(value || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const labelDisplayName = label =>
  ARM_GOLD_LABEL_TRANSLATIONS[label] || label.replaceAll('-', ' ');

const parsedLabelActivity = computed(() => {
  const structured =
    contentAttributes.value?.armgoldLabelActivity ||
    contentAttributes.value?.armgold_label_activity;

  if (structured?.labels?.length) {
    return {
      userName: structured.userName || structured.user_name || 'النظام',
      changeType: structured.changeType || structured.change_type || 'added',
      labels: structured.labels,
    };
  }

  const text = stripHtml(content.value);
  const match = text.match(/^(.+?)\s+(added|removed)\s+(.+)$/i);
  if (!match) return null;

  return {
    userName: match[1],
    changeType: match[2].toLowerCase(),
    labels: match[3]
      .split(',')
      .map(label => label.trim())
      .filter(Boolean),
  };
});

const isLabelActivity = computed(() => !!parsedLabelActivity.value);

const changeTypeText = computed(() =>
  parsedLabelActivity.value?.changeType === 'removed' ? 'أزال' : 'أضاف'
);

const openAiQuotePanel = label => {
  window.dispatchEvent(
    new CustomEvent('armgold:open-ai-quote', {
      detail: { label },
    })
  );
};
</script>

<template>
  <BaseBubble
    v-tooltip.top="readableTime"
    class="px-3 py-1 !rounded-xl flex min-w-0 items-center gap-2"
    data-bubble-name="activity"
  >
    <span v-if="isLabelActivity" class="armgold-label-activity" dir="auto">
      <span class="armgold-label-activity-text">
        {{ parsedLabelActivity.userName }} {{ changeTypeText }}
      </span>
      <button
        v-for="label in parsedLabelActivity.labels"
        :key="label"
        type="button"
        class="armgold-label-chip"
        :title="openAiTitle"
        @click="openAiQuotePanel(label)"
      >
        {{ labelDisplayName(label) }}
      </button>
    </span>
    <span v-else v-dompurify-html="content" :title="content" />
  </BaseBubble>
</template>

<style scoped>
.armgold-label-activity {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  min-width: 0;
}

.armgold-label-activity-text {
  color: var(--color-body-muted);
}

.armgold-label-chip {
  max-width: 10rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0.125rem 0.375rem;
  border: 1px solid var(--color-border);
  border-radius: var(--border-radius-small);
  background: var(--color-background);
  color: var(--color-body);
  font-size: var(--font-size-micro);
  line-height: 1.35;
}

.armgold-label-chip {
  cursor: pointer;
  border-color: var(--b-400);
}

.armgold-label-chip:hover {
  background: var(--b-50);
  color: var(--b-700);
}
</style>
